import {
  collection, query, getDocs, doc, getDoc, updateDoc, deleteDoc, setDoc,
  orderBy, limit, where, serverTimestamp, writeBatch, addDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../lib/firebase/app';
import type { User, DeletedUser, BatteryTransaction } from '../types';

export async function getAllUsers(): Promise<User[]> {
  const snap = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => ({ uid: d.id, ...d.data() } as User));
}

export async function getUser(uid: string): Promise<User | null> {
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) return null;
  return { uid: snap.id, ...snap.data() } as User;
}

export async function updateUser(uid: string, data: Partial<User>): Promise<void> {
  await updateDoc(doc(db, 'users', uid), { ...data, updatedAt: serverTimestamp() });
}

/**
 * Delete user(s) via adminBatchDeleteUsers Cloud Function.
 * The function handles: read → backup to deletedUsers → delete Firestore doc → delete Auth.
 */
export async function deleteUser(uid: string, reason = 'admin_deleted'): Promise<void> {
  await deleteMultipleUsers([uid], reason);
}

export async function deleteMultipleUsers(uids: string[], reason = 'admin_batch_delete'): Promise<{ succeeded: number; failed: number }> {
  const fn = httpsCallable<{ uids: string[]; reason: string }, { succeeded: number; failed: number }>(
    functions, 'adminBatchDeleteUsers'
  );
  const result = await fn({ uids, reason });
  return result.data;
}

export async function grantAdmin(uid: string): Promise<void> {
  await updateUser(uid, { isAdmin: true } as Partial<User>);
}

export async function revokeAdmin(uid: string): Promise<void> {
  await updateUser(uid, { isAdmin: false } as Partial<User>);
}

export async function grantOperator(uid: string): Promise<void> {
  await updateUser(uid, { isOperator: true } as Partial<User>);
}

export async function revokeOperator(uid: string): Promise<void> {
  await updateUser(uid, { isOperator: false } as Partial<User>);
}

export async function lockChat(uid: string): Promise<void> {
  await updateUser(uid, { chatLocked: true } as Partial<User>);
}

export async function unlockChat(uid: string): Promise<void> {
  await updateUser(uid, { chatLocked: false } as Partial<User>);
}

export async function setBattery(uid: string, amount: number, adminId: string): Promise<void> {
  const user = await getUser(uid);
  const prevBattery = user?.battery ?? 0;
  const delta = amount - prevBattery;
  await updateDoc(doc(db, 'users', uid), { battery: amount, updatedAt: serverTimestamp() });
  await addBatteryTransaction(uid, { type: 'ADMIN_CHARGE', amount: delta, timestamp: Date.now(), balance: amount });
}

export async function adjustBattery(uid: string, delta: number, adminId: string): Promise<void> {
  const user = await getUser(uid);
  if (!user) return;
  const newAmount = Math.max(0, user.battery + delta);
  await updateDoc(doc(db, 'users', uid), { battery: newAmount, updatedAt: serverTimestamp() });
  await addBatteryTransaction(uid, {
    type: delta > 0 ? 'ADMIN_CHARGE' : 'SYSTEM',
    amount: delta,
    timestamp: Date.now(),
    balance: newAmount,
  });
}

/** Bulk battery adjust (positive=charge, negative=deduct) */
export async function bulkAdjustBattery(uids: string[], delta: number, adminId: string): Promise<number> {
  let count = 0;
  for (const uid of uids) {
    await adjustBattery(uid, delta, adminId);
    count++;
  }
  return count;
}

/** Bulk set battery to absolute value */
export async function bulkSetBattery(uids: string[], amount: number, adminId: string): Promise<number> {
  let count = 0;
  for (const uid of uids) {
    await setBattery(uid, amount, adminId);
    count++;
  }
  return count;
}

async function addBatteryTransaction(uid: string, tx: BatteryTransaction) {
  const { addDoc, collection: col } = await import('firebase/firestore');
  await addDoc(col(db, 'users', uid, 'batteryHistory'), tx);
}

export async function getBatteryHistory(uid: string): Promise<BatteryTransaction[]> {
  const snap = await getDocs(
    query(collection(db, 'users', uid, 'batteryHistory'), orderBy('timestamp', 'desc'), limit(200))
  );
  return snap.docs.map(d => d.data() as BatteryTransaction);
}

export async function findUserByNickname(nickname: string): Promise<User | null> {
  const q = query(collection(db, 'users'), where('nickname', '==', nickname), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { uid: snap.docs[0].id, ...snap.docs[0].data() } as User;
}

/** Search users whose nickname contains the keyword (partial match, client-side filter) */
export async function searchUsersByNickname(keyword: string, maxResults = 20): Promise<User[]> {
  if (!keyword) return [];
  const snap = await getDocs(query(collection(db, 'users'), limit(500)));
  const kw = keyword.toLowerCase();
  return snap.docs
    .map(d => ({ uid: d.id, ...d.data() } as User))
    .filter(u => u.nickname?.toLowerCase().includes(kw))
    .slice(0, maxResults);
}

/** Find user by email */
export async function findUserByEmail(email: string): Promise<User | null> {
  const q = query(collection(db, 'users'), where('email', '==', email), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return { uid: snap.docs[0].id, ...snap.docs[0].data() } as User;
}

export async function searchUsers(keyword: string): Promise<User[]> {
  const all = await getAllUsers();
  const kw = keyword.toLowerCase();
  return all.filter(u =>
    u.nickname?.toLowerCase().includes(kw) ||
    u.email?.toLowerCase().includes(kw) ||
    u.uid.toLowerCase().includes(kw)
  );
}

export async function getDeletedUsers(): Promise<DeletedUser[]> {
  const snap = await getDocs(collection(db, 'deletedUsers'));
  return snap.docs
    .map(d => ({ docId: d.id, ...d.data() } as DeletedUser))
    .sort((a, b) => (b.deletedAt || 0) - (a.deletedAt || 0));
}

/** Remove re-registration restriction by setting deletedAt to 0 */
export async function liftDeletionRestriction(email: string): Promise<number> {
  const q = query(collection(db, 'deletedUsers'), where('email', '==', email));
  const snap = await getDocs(q);
  let count = 0;
  for (const d of snap.docs) {
    await setDoc(d.ref, { deletedAt: 0 }, { merge: true });
    count++;
  }
  return count;
}

/** Remove re-registration restriction for a specific document */
export async function liftDeletionRestrictionByDocId(docId: string): Promise<void> {
  await setDoc(doc(db, 'deletedUsers', docId), { deletedAt: 0 }, { merge: true });
}

/** Delete a deletedUsers document permanently */
export async function removeDeletedUserRecord(docId: string): Promise<void> {
  await deleteDoc(doc(db, 'deletedUsers', docId));
}

export async function deleteAllBotUsers(): Promise<number> {
  const q = query(collection(db, 'users'), where('isBot', '==', true));
  const snap = await getDocs(q);
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
  return snap.size;
}

export function getUserTypeLabel(user: User): string {
  if (user.isAdmin) return '관리자';
  if (user.isOperator) return '운영자';
  if (user.isBot) return '봇';
  return '일반';
}

export function getUserTypeBadgeColor(user: User): string {
  if (user.isAdmin) return 'bg-red-100 text-red-800';
  if (user.isOperator) return 'bg-purple-100 text-purple-800';
  if (user.isBot) return 'bg-gray-100 text-gray-600';
  return 'bg-blue-100 text-blue-800';
}
